import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { getFloorName } from "../../../../utils/helpers-for-filter";
import styles from "./card-summary.module.scss";
import logo from "../../../../images/headerLogo.svg";
import Infrastructure from "../../../map-controls/infrastructure/infrastructure";
import QrCode from "../../../../shared/qr-code/qr-code";
import { CutAndFill } from "../../../../interfaces/feature-info";
import { InfrastructureType } from "../../../../hooks/useInfrastructure";
import {
  InvestmentOpportunity,
  LandPlot,
  SpaceForRent,
} from "../../../../interfaces/card";

interface CardSummaryProps<
  T extends LandPlot | SpaceForRent | InvestmentOpportunity,
> {
  item: T;
  type:
    | "InvestmentOpportunities"
    | "Plots"
    | "SpacesForRent"
    | "MultiplePlotsList";
  cutAndFill?: CutAndFill | null;
  floorPlan?: string | null;
  screenshot: string | null;
  cutAndFillImage?: string | null;
  infrastructure?: InfrastructureType;
}

const CardSummary = forwardRef<
  HTMLDivElement,
  CardSummaryProps<LandPlot | SpaceForRent | InvestmentOpportunity>
>(
  (
    {
      item,
      type,
      cutAndFill,
      floorPlan,
      screenshot,
      cutAndFillImage,
      infrastructure,
    },
    ref,
  ) => {
    const { t, i18n } = useTranslation();
    const getEstimatedAnnualRent = () => {
      if (type === "SpacesForRent") {
        const space = item as SpaceForRent;
        return `${space.annualRateMin} - ${space.annualRateMax} OMR`;
      }

      const annualRate = (item as LandPlot | InvestmentOpportunity).annualRate;
        return annualRate != null ? `${annualRate} OMR` : "0.000 OMR";
    };

    const boldFirstWord = (str: string) => {
      if (!str) return null;
      const words = str.split(" ");
      if (words.length === 0) return str;
      return (
        <>
          <strong>{words[0]}</strong> {words.slice(1).join(" ")}
        </>
      );
    };

    const renderTitle = () => {
      switch (type) {
        case "Plots":
          return t("city-card.plotSummary");
        case "SpacesForRent":
          return t("city-card.spaceForRentSummary");
        case "InvestmentOpportunities":
          return t("city-card.investmentOpportunitySummary");
        case "MultiplePlotsList":
          return t("city-card.multiplePlotSummary");
        default:
          return null;
      }
    };

    const renderDetails = () => {
      switch (type) {
        case "Plots":
        case "MultiplePlotsList":
        case "InvestmentOpportunities":
          const isLandPlot = type === "Plots" || type === "MultiplePlotsList";
          const plotOrInvOp = item as LandPlot | InvestmentOpportunity;

          return (
            <>
              <span className={styles.titleDesc}>
                {isLandPlot
                  ? `${type === "MultiplePlotsList" ? t("city-card.multiplePlotNumbers") : t("city-card.plotNumberLabel")} ${plotOrInvOp.number} ${t("city-card.description")}:`
                  : `${"invOpNameEn" in plotOrInvOp && plotOrInvOp.invOpNameEn} ${t("city-card.description")}:`}
              </span>
              <div className={styles.description}>
                <div className={styles.descSection}>
                  <span>
                    {t("city-card.city")}: {plotOrInvOp.city}
                  </span>
                  {isLandPlot ? (
                    <span>
                      {t("city-card.phaseNumber")}:{" "}
                      {"phase" in plotOrInvOp && plotOrInvOp.phase}
                    </span>
                  ) : (
                    <span>
                      {t("city-card.plotNumber")}: {plotOrInvOp.number}
                    </span>
                  )}
                  <span>
                    {t("city-card.plotArea")}: {plotOrInvOp.area} m²
                  </span>
                </div>
                <div className={styles.descSection}>
                  <span>
                    {t("city-card.landStatus")}: {plotOrInvOp.status}
                  </span>
                  <span>
                    {t("city-card.landUse")}: {plotOrInvOp.typeOfUse}
                  </span>
                  <span>
                    {t("city-card.estimatedAnnualRent")}: {getEstimatedAnnualRent()}
                  </span>
                </div>
              </div>
              {infrastructure && (
                <div className={styles.infrastructure}>
                  <span>{t("city-card.infrastructureAvailability")}:</span>
                  <Infrastructure infrastructure={infrastructure} />
                </div>
              )}
              <div className={styles.analysicsDetails}>
                {cutAndFill &&
                  cutAndFillImage && (
                    <div className={styles.cutAndFill}>
                      <span>{t("city-card.cutAndFill")}:</span>
                      <div className={styles.imageWrapper}>
                        <img
                          className={styles.cutAndFillImage}
                          src={cutAndFillImage}
                          alt="cutAndFill"
                        />
                      </div>
                      <div className={styles.calculation}>
                        <p>
                          {t("city-card.landCut")}: {cutAndFill?.landCut} m³
                        </p>
                        <p>
                          {t("city-card.landFill")}: {cutAndFill?.landFill} m³
                        </p>
                      </div>
                    </div>
                  )}
                {screenshot && (
                  <div className={styles.cutAndFill}>
                    <span>{t("city-card.plotBoundaries")}:</span>
                    <div className={styles.imageWrapper}>
                      <img
                        className={styles.screenshotImage}
                        src={screenshot}
                        alt="cutAndFill"
                      />
                    </div>
                    <div className={styles.calculation}>
                      {type === "MultiplePlotsList" ? (
                        <p>
                          {t("city-card.multiplePlotNumbers")}:{" "}
                          {plotOrInvOp.number} {t("city-card.location")}
                        </p>
                      ) : (
                        <p>
                          {t("city-card.plotNumber")}: {plotOrInvOp.number}{" "}
                          {t("city-card.location")}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          );
        case "SpacesForRent":
          const space = item as SpaceForRent;
          return (
            <>
              <span className={styles.titleDesc}>
                {t("city-card.officeNumber")}: {space.number}{" "}
                {t("city-card.description")}:
              </span>
              <div className={styles.description}>
                <div className={styles.descSection}>
                  <span>
                    {t("city-card.city")}: {space.city}
                  </span>
                  <span>
                    {t("city-card.floor")}: {getFloorName(space, i18n.language, t("city-card.noData"))}
                  </span>
                  <span>
                    {t("city-card.spaceArea")}: {space.area} m²
                  </span>
                </div>
                <div className={styles.descSection}>
                  <span>
                    {t("city-card.landStatus")}: {space.status}
                  </span>
                  <span>
                    {t("city-card.landUse")}: {space.typeOfUse}
                  </span>
                  <span>
                    {t("city-card.estimatedAnnualRent")}: {getEstimatedAnnualRent()}
                  </span>
                </div>
              </div>
              <div className={styles.analysicsDetails}>
                {floorPlan && (
                  <div className={styles.floorPlan}>
                    <span>{t("city-card.floorPlan")}:</span>
                    {floorPlan && (
                      <img
                        className={styles.floorPlan}
                        src={floorPlan}
                        alt="floorPlan"
                      />
                    )}
                  </div>
                )}
                {screenshot && (
                  <div className={styles.floorPlan}>
                    <span>{t("city-card.buildingBoundaries")}:</span>
                    {screenshot && <img src={screenshot} alt="screenshot" />}
                  </div>
                )}
              </div>
            </>
          );
        default:
          return null;
      }
    };

    return (
      <div ref={ref} className={styles.card}>
        <div className={styles.header}>
          <div className={styles.section}>
            <h4>{t("card-krookie.sultanate")}</h4>
            <p>{t("card-krookie.operationsDept")}</p>
            <span>{boldFirstWord(item.city)}</span>
          </div>
          <img src={logo} alt="logo" />
          <div className={styles.section}>
            <h4>{t("card-krookie.sultanate")}</h4>
            <p>{t("card-krookie.operationsDept")}</p>
            <span>{boldFirstWord(item.estateAr)}</span>
          </div>
        </div>
        <div className={styles.title}>
          <h1>{renderTitle()}</h1>
          {type !== "MultiplePlotsList" && (
            <div className={styles.qr}>
              <QrCode
                href={window.location.href}
                sizeQr={72}
                qrOnly={true}
                from={"search"}
              />
            </div>
          )}
        </div>
        {renderDetails()}
      </div>
    );
  },
);

export default CardSummary;
